import React, { useCallback, useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrashAlt,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

interface DynamicListInputProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  addButtonLabel?: string;
}

export default function DynamicListInput({
  label,
  items,
  onChange,
  placeholder = "Enter value",
  addButtonLabel = "Add Item",
}: DynamicListInputProps) {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [focusNextIndex, setFocusNextIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus the next input when needed
  useEffect(() => {
    if (focusNextIndex !== null && containerRef.current) {
      const inputs = containerRef.current.querySelectorAll("input");
      const targetInput = inputs[focusNextIndex] as HTMLInputElement;
      if (targetInput) {
        requestAnimationFrame(() => {
          targetInput.focus();
        });
      }
      setFocusNextIndex(null);
    }
  }, [focusNextIndex, items]);

  const handleItemChange = useCallback(
    (index: number, value: string) => {
      const newItems = [...items];
      newItems[index] = value;
      onChange(newItems);
    },
    [items, onChange]
  );

  const handleItemBlur = useCallback((index: number) => {
    setFocusedIndex(null);
  }, []);

  const handleItemFocus = useCallback((index: number) => {
    setFocusedIndex(index);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const newItems = [...items];
        newItems.splice(index + 1, 0, "");
        onChange(newItems);
        setFocusNextIndex(index + 1);
      }
    },
    [items, onChange]
  );

  const addItem = useCallback(() => {
    const newItems = [...items, ""];
    onChange(newItems);
  }, [items, onChange]);

  const removeItem = useCallback(
    (index: number) => {
      const newItems = items.filter((_, i) => i !== index);
      onChange(newItems);
    },
    [items, onChange]
  );

  return (
    <div>
      <h3 className="text-sm uppercase  tracking-widest text-slate-700 dark:text-slate-300 mb-2 font-mono">
        {label}
      </h3>
      <div ref={containerRef} className="space-y-3">
        {items.map((item, index) => (
          <div key={`item-${index}`} className="flex items-stretch gap-2">
            <FormInput
              value={item}
              placeholder={placeholder}
              onChange={(e) => handleItemChange(index, e.target.value)}
              onFocus={() => handleItemFocus(index)}
              onBlur={() => handleItemBlur(index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
            />
            <div
              onClick={() => removeItem(index)}
              className="flex self-stretch items-center flex-none h-9 rounded-xl aspect-square justify-center hover:bg-red-400/10 hover:text-red-400"
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </div>
          </div>
        ))}
      </div>
      <Button onClick={addItem} className="mt-3">
        <FontAwesomeIcon icon={faPlus} />
        {addButtonLabel}
      </Button>
    </div>
  );
}
